import { useState } from "react"
import { SwatchBook } from "lucide-react"

import {
  useProductVisualDirection,
  useSetProductVisualDirection,
} from "../../services/use-visual-directions"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { Combobox, type ComboboxOption } from "../ui/combobox"

export function ProductVisualDirectionControl({ configSku }: { configSku: string }) {
  const visualDirection = useProductVisualDirection(configSku)
  const select = useSetProductVisualDirection(configSku)

  if (visualDirection.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading Visual Direction selection…</p>
  }
  if (visualDirection.error) {
    return (
      <Callout tone="destructive" dense title="Visual Direction selection unavailable">
        {visualDirection.error.message}
      </Callout>
    )
  }
  if (!visualDirection.data) {
    return null
  }

  return <ProductVisualDirectionPanel data={visualDirection.data} select={select} />
}

type ProductVisualDirectionData = NonNullable<ReturnType<typeof useProductVisualDirection>["data"]>
type ProductVisualDirectionMutation = ReturnType<typeof useSetProductVisualDirection>

function ProductVisualDirectionPanel({
  data,
  select,
}: {
  data: ProductVisualDirectionData
  select: ProductVisualDirectionMutation
}) {
  const [pendingVersion, setPendingVersion] = useState<string | null | undefined>(undefined)
  const [confirmed, setConfirmed] = useState(false)

  const { state, compatibleVersions } = data
  const selectedId = state.selection?.selection.versionId ?? null
  const effectivePending = pendingVersion === undefined ? selectedId : pendingVersion
  const changed = effectivePending !== selectedId
  const selectedIsArchived = isArchivedCurrentPin(data, selectedId)
  const archivedOption = archivedCurrentPinOption(data, selectedIsArchived)
  const options: ComboboxOption[] = [
    ...(archivedOption ? [archivedOption] : []),
    ...compatibleVersions.map(({ direction, version }) => ({
      value: version.id,
      label: `${direction.name} · v${version.version}`,
      keywords: direction.name,
    })),
  ]

  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <SwatchBook className="size-4" />
          </span>

          <div>
            <h2 className="text-sm font-semibold">Visual Direction</h2>

            <p className="mt-1 text-xs text-muted-foreground">
              {state.brandFamily} · optional · independent of marketplace
            </p>
          </div>
        </div>

        <Combobox
          aria-label="Product Visual Direction"
          className="min-w-64"
          value={effectivePending ?? undefined}
          options={options}
          placeholder="No Visual Direction"
          clearLabel="No Visual Direction"
          searchPlaceholder="Search Visual Directions…"
          emptyMessage="No compatible Visual Directions"
          onChange={(value) => {
            setPendingVersion(value ?? null)
            setConfirmed(false)
          }}
        />
      </div>

      <SelectionSuggestion data={data} />

      {changed ? (
        <div className="mt-3 rounded-lg bg-warning/10 p-3">
          <p className="text-xs text-foreground/80">
            Changing the direction preserves candidates and last-approved values. Current approvals
            become stale. Nothing regenerates or publishes.
          </p>

          <label className="mt-2 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />{" "}
            Apply this selection change.
          </label>

          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={!confirmed || select.isPending}
              onClick={() =>
                void select.mutateAsync(effectivePending).then(() => {
                  setPendingVersion(undefined)
                  setConfirmed(false)
                })
              }
            >
              Save selection
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPendingVersion(undefined)
                setConfirmed(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {select.error ? (
        <p className="mt-2 text-xs text-destructive">{select.error.message}</p>
      ) : null}
    </section>
  )
}

function isArchivedCurrentPin(
  data: ProductVisualDirectionData,
  selectedId: string | null
): boolean {
  if (!selectedId || !data.state.selection?.version || !data.state.selection.direction) {
    return false
  }
  return !data.compatibleVersions.some(({ version }) => version.id === selectedId)
}

function archivedCurrentPinOption(
  data: ProductVisualDirectionData,
  visible: boolean
): ComboboxOption | null {
  const selected = data.state.selection
  if (!visible || !selected?.version || !selected.direction) {
    return null
  }
  return {
    value: selected.version.id,
    label: `${selected.direction.name} · v${selected.version.version} · Archived current pin`,
    keywords: selected.direction.name,
    disabled: true,
  }
}

function SelectionSuggestion({ data }: { data: ProductVisualDirectionData }) {
  const suggestion = data.state.suggestion
  return suggestion ? (
    <p className="mt-3 text-xs text-muted-foreground">
      {`Explicit default: ${suggestion.direction.name} · v${suggestion.version.version}. Nova suggests only this version.`}
    </p>
  ) : (
    <p className="mt-3 text-xs text-muted-foreground">
      No explicit default. Nova suggests no direction.
    </p>
  )
}
